import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, isRedirect, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import {
  AppShell,
  Avatar,
  DiscardChangesDialog,
  freshState,
  KiteMark,
  ModeSwitch,
  rememberPlatformPage,
  resumeTarget,
  Sidebar,
  SidebarItem,
  switchModes,
  switchTarget,
  useTheme,
} from "@vd/ui";
import {
  Building,
  CircleDollarSign,
  ClipboardCheck,
  Inbox,
  LogOut,
  MessageSquareWarning,
  Moon,
  Package,
  PhoneForwarded,
  ScrollText,
  ServerCog,
  SquareTerminal,
  Sun,
  UsersRound,
} from "lucide-react";
import { type ComponentType, useEffect, useState } from "react";
import { ApiError, errorMessage, setCsrfToken, setUnauthorizedHandler } from "../api/client";
import { auth, meQuery, qk } from "../api/ops";
import { useMe } from "../auth/useMe";
import { PasskeyNotice } from "../components/PasskeyNotice";
import { RouteCrumbs } from "../lib/crumbs";
import { handoffInFragment, redeemHandoff, startHandoff } from "../lib/handoff";
import { useNavigate } from "../lib/navigate";
import { setOwner, useBackGestures, useLevels } from "../lib/navTrail";

export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }) => {
    // Back from the customer app with a one-time handoff (migration 0203): redeem it with this
    // tab's verifier, then open the page the switch asked for.
    const handoff = location.pathname === "/resume" ? handoffInFragment(location.hash) : null;
    if (handoff) {
      try {
        const { me, to } = await redeemHandoff(handoff);
        setCsrfToken(me.csrf_token);
        context.queryClient.setQueryData(qk.me, me);
        throw redirect({ href: resumeTarget("platform", to), replace: true });
      } catch (e) {
        if (isRedirect(e)) throw e;
        throw redirect({ to: "/signin", search: { error: errorMessage(e) }, replace: true });
      }
    }
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        // The mode switch brought a signed-out person here: ask the customer app for a handoff.
        // Passkey sign-in stays one click away on the sign-in page.
        const search = location.search as { to?: string; from?: string };
        if (location.pathname === "/resume" && (search.from === "app" || search.from === "admin")) {
          try {
            await startHandoff(typeof search.to === "string" ? search.to : null);
            return new Promise<never>(() => {});
          } catch {
            // Fall through to the sign-in page.
          }
        }
        throw redirect({ to: "/signin", search: { redirect: location.href } });
      }
      throw e;
    }
  },
  component: AppLayout,
});

type NavTo =
  | "/companies"
  | "/readiness"
  | "/demo-requests"
  | "/reports"
  | "/costs"
  | "/parallel-dialing"
  | "/releases"
  | "/logs"
  | "/server"
  | "/staff"
  | "/audit";

// Platform is its own app: the staff sections themselves, no page named after the mode
// (DESIGN.md "Mode switch" and "Platform providers").
const NAV: { to: NavTo; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { to: "/companies", label: "Companies", icon: Building },
  { to: "/reports", label: "Reports", icon: MessageSquareWarning },
  { to: "/demo-requests", label: "Demo requests", icon: Inbox },
  { to: "/readiness", label: "Readiness", icon: ClipboardCheck },
  { to: "/costs", label: "Costs", icon: CircleDollarSign },
  { to: "/parallel-dialing", label: "Parallel dialing", icon: PhoneForwarded },
  { to: "/releases", label: "Releases", icon: Package },
  { to: "/logs", label: "Logs", icon: SquareTerminal },
  { to: "/server", label: "Server", icon: ServerCog },
  { to: "/staff", label: "Staff", icon: UsersRound },
  { to: "/audit", label: "Audit", icon: ScrollText },
];

function AppLayout() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const here = useRouterState({ select: (s) => s.location.href });
  // Where this tab was on the ops console, for the switch's way back (its own host's memory).
  useEffect(() => rememberPlatformPage(here), [here]);
  const modes = switchModes(me.customer_modes ?? { admin: false, app: false }, "platform");
  // The section where the trail started stays lit (ADR 0030).
  const trailTop = useLevels().above[0]?.href;
  const lit = trailTop ? (trailTop.split(/[?#]/)[0] ?? pathname) : pathname;
  const narrow = useNarrow();
  const { resolved, setTheme } = useTheme();
  useBackGestures();
  useEffect(() => {
    setOwner(me.email);
    return () => setOwner(null);
  }, [me.email]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      qc.removeQueries({ queryKey: qk.me });
      navigate({
        to: "/signin",
        search: { redirect: window.location.pathname + window.location.search },
      });
    });
    return () => setUnauthorizedHandler(null);
  }, [qc, navigate]);

  const signOut = async () => {
    try {
      await auth.logout();
    } catch {
      // Sign out locally either way.
    }
    setCsrfToken(null);
    qc.clear();
    navigate({ to: "/signin" });
  };

  const go = (e: React.MouseEvent) => {
    // Sidebar items are real links; plain left clicks route in-app.
    const a = (e.target as Element).closest("a");
    if (!a || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const href = a.getAttribute("href");
    if (!href?.startsWith("/")) return;
    e.preventDefault();
    // A fresh start: the page offers no "Back to", even within its section.
    navigate({ href, state: freshState() });
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: delegates clicks on the links inside
    // biome-ignore lint/a11y/useKeyWithClickEvents: links keep their own keyboard behavior
    <div className="h-full" onClick={go}>
      <AppShell
        sidebar={
          <Sidebar
            collapsed={narrow}
            header={
              <div className={narrow ? "flex flex-col items-center gap-2" : "flex flex-col gap-2.5"}>
                <div
                  className={
                    narrow ? "flex flex-col items-center gap-1" : "flex h-8 items-center gap-2 px-1.5"
                  }
                >
                  <KiteMark size={20} tone="on-dark" />
                  {narrow ? null : <span className="font-medium text-ui">Kite</span>}
                  {/* The lit Platform segment marks Kite's own console; without a switch, the tag. */}
                  {modes.length < 2 && !narrow ? (
                    <span className="font-medium text-ink-3 text-micro">Platform</span>
                  ) : null}
                </div>
                {/* App and Admin are the customer app, on its own host: their links load it. */}
                <ModeSwitch
                  current="platform"
                  modes={modes}
                  collapsed={narrow}
                  onSwitch={() => false}
                  hrefFor={(m) => switchTarget(m, { current: "platform", here, appUrl: me.app_url ?? "" })}
                />
              </div>
            }
            footer={
              <>
                {narrow ? null : (
                  <div className="flex items-center gap-2.5 px-2.5 pt-1 pb-2">
                    <Avatar name={me.email} seed={me.email} size="sm" aria-hidden />
                    <p className="min-w-0 truncate text-caption text-ink-2">{me.email}</p>
                  </div>
                )}
                <SidebarItem
                  icon={resolved === "dark" ? <Sun /> : <Moon />}
                  label={resolved === "dark" ? "Light theme" : "Dark theme"}
                  onClick={() => setTheme(resolved === "dark" ? "light" : "dark")}
                />
                <SidebarItem icon={<LogOut />} label="Sign out" onClick={signOut} />
              </>
            }
          >
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <SidebarItem
                  key={n.to}
                  href={n.to}
                  icon={<Icon />}
                  label={n.label}
                  active={lit === n.to || lit.startsWith(`${n.to}/`)}
                />
              );
            })}
          </Sidebar>
        }
      >
        <div className="max-w-[1280px] px-4 py-5 sm:px-6 sm:py-6">
          {me.passkey === false ? <PasskeyNotice /> : null}
          <RouteCrumbs />
          <Outlet />
          <DiscardChangesDialog />
        </div>
      </AppShell>
    </div>
  );
}

function useNarrow(query = "(max-width: 1023px)"): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return narrow;
}
